import axios from 'axios';

/**
 * Sage 300 Adapter Service
 * Encapsulates HTTP calls to the Sage 300 Middleware API.
 * All endpoints (except discover) require X-Api-Key header.
 * Base URL: window.sage300AdapterApi (defined in GlobalVariables.js)
 */

const baseUrl = () => window.sage300AdapterApi || 'https://lna.pos.co.zm/lytsageapi/';

const authHeaders = (apiKey) => ({ 'X-Api-Key': apiKey });

// ── Configuration endpoints ──────────────────────────────────────

/**
 * Discover Sage 300 master data (no API key required).
 * Verifies credentials and returns warehouses, customers, price lists, currencies.
 */
export const discoverSage300 = async (credentials) => {
    try {
        const { data } = await axios.post(`${baseUrl()}config/discover`, credentials);
        return data;
    } catch (error) {
        console.error('Sage 300 discover error:', error);
        throw error;
    }
};

/**
 * Register a branch config and receive an API key.
 */
export const registerSage300 = async (payload) => {
    try {
        const { data } = await axios.post(`${baseUrl()}config/register`, payload);
        return data;
    } catch (error) {
        console.error('Sage 300 register error:', error);
        throw error;
    }
};

/**
 * Register multiple branches at once.
 */
export const registerBulkSage300 = async (apiKey, payload) => {
    try {
        const { data } = await axios.post(`${baseUrl()}config/register-bulk`, payload, {
            headers: authHeaders(apiKey)
        });
        return data;
    } catch (error) {
        console.error('Sage 300 register-bulk error:', error);
        throw error;
    }
};

// ── Data endpoints ───────────────────────────────────────────────

/**
 * Fetch items from Sage 300 middleware.
 * Supports OData params: $top, $skip, $select, $filter
 */
export const fetchSage300Items = async (apiKey, params = {}) => {
    try {
        const { data } = await axios.get(`${baseUrl()}items`, {
            headers: authHeaders(apiKey),
            params
        });
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.items)) return data.items;
        return [];
    } catch (error) {
        console.error('Sage 300 fetchItems error:', error);
        throw error;
    }
};

/**
 * Fetch a single item by item number.
 */
export const fetchSage300Item = async (apiKey, itemNumber) => {
    try {
        const { data } = await axios.get(`${baseUrl()}items/${encodeURIComponent(itemNumber)}`, {
            headers: authHeaders(apiKey)
        });
        return data;
    } catch (error) {
        console.error('Sage 300 fetchItem error:', error);
        throw error;
    }
};

/**
 * Fetch customers from Sage 300 middleware.
 * Supports OData params: $top, $skip, $select, $filter
 */
export const fetchSage300Customers = async (apiKey, params = {}) => {
    try {
        const { data } = await axios.get(`${baseUrl()}customers`, {
            headers: authHeaders(apiKey),
            params
        });
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.customers)) return data.customers;
        return [];
    } catch (error) {
        console.error('Sage 300 fetchCustomers error:', error);
        throw error;
    }
};

/**
 * Fetch a single customer by customer number.
 */
export const fetchSage300Customer = async (apiKey, customerNumber) => {
    try {
        const { data } = await axios.get(`${baseUrl()}customers/${encodeURIComponent(customerNumber)}`, {
            headers: authHeaders(apiKey)
        });
        return data;
    } catch (error) {
        console.error('Sage 300 fetchCustomer error:', error);
        throw error;
    }
};

// ── Sales ────────────────────────────────────────────────────────

/**
 * Create an OE Invoice in Sage 300 (idempotent via externalId).
 */
export const postSage300Sale = async (apiKey, payload) => {
    try {
        const { data } = await axios.post(`${baseUrl()}sales`, payload, {
            headers: authHeaders(apiKey)
        });
        return data;
    } catch (error) {
        console.error('Sage 300 postSale error:', error);
        throw error;
    }
};

// ── Sync ─────────────────────────────────────────────────────────

/**
 * Start a Sage 300 sync job.
 * @param {string} path - Sync endpoint path (e.g., '/sync/items', '/sync/stock-qty')
 * @param {string} apiKey - Sage 300 API key
 * @param {object} payload - { fullSync, fromDate, batchSize, dryRun }
 */
export const startSage300Sync = async (path, apiKey, payload = {}) => {
    try {
        const url = `${baseUrl()}${path.replace(/^\//, '')}`;
        const { data } = await axios.post(url, payload, {
            headers: authHeaders(apiKey)
        });
        return data;
    } catch (error) {
        console.error(`Sage 300 sync error (${path}):`, error);
        throw error;
    }
};

// ── Audit Logs ───────────────────────────────────────────────────

/**
 * Fetch audit logs from middleware.
 * @param {string} apiKey
 * @param {object} params - { size, page, method, uri }
 */
export const fetchSage300Logs = async (apiKey, params = {}) => {
    try {
        const { data } = await axios.get(`${baseUrl()}logs`, {
            headers: authHeaders(apiKey),
            params
        });
        return data;
    } catch (error) {
        console.error('Sage 300 fetchLogs error:', error);
        throw error;
    }
};

/**
 * Fetch audit logs filtered by group/branch.
 */
export const fetchSage300LogsByGroup = async (apiKey, group, params = {}) => {
    try {
        const { data } = await axios.get(`${baseUrl()}logs/${encodeURIComponent(group)}`, {
            headers: authHeaders(apiKey),
            params
        });
        return data;
    } catch (error) {
        console.error('Sage 300 fetchLogsByGroup error:', error);
        throw error;
    }
};

export default {
    discoverSage300,
    registerSage300,
    registerBulkSage300,
    fetchSage300Items,
    fetchSage300Item,
    fetchSage300Customers,
    fetchSage300Customer,
    postSage300Sale,
    startSage300Sync,
    fetchSage300Logs,
    fetchSage300LogsByGroup
};
